import { Injectable, DOCUMENT, inject } from '@angular/core'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
// eslint-disable-next-line @nx/enforce-module-boundaries
import { Palette } from '@wm-front/shared/interfaces'
import { fromEvent, Observable, ReplaySubject } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import { ChartColors } from '../interfaces/chart-colors.interface'

/** Enum with all the UI Breakpoint values */
export enum Breakpoint {
  Tiny = 0,
  Small = 480,
  Medium = 768,
  Large = 1024,
  ExtraLarge = 1400,
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  protected _document = inject<Document>(DOCUMENT)

  /** Currently active breakpoint */
  private _activeBreakpoint$: ReplaySubject<Breakpoint> = new ReplaySubject()

  /** Current screen size */
  private _currentScreenSize$: ReplaySubject<number> = new ReplaySubject()

  /** Design tokens backing the dominant palette (see libs/shared/styles/sass/tokens/_semantic.scss) */
  private readonly _paletteTokens: Palette = {
    primary: '--wm-color-brand-primary',
    success: '--wm-color-feedback-success',
    info: '--wm-color-feedback-info',
    warning: '--wm-color-feedback-warning',
    danger: '--wm-color-feedback-danger',
    sky: '--wm-color-accent-sky',
  }

  /** Values used when the tokens are not available (unit tests, styles not loaded) */
  private readonly _paletteFallback: Palette = {
    primary: '#272c6c',
    success: '#50c392',
    info: '#0081c5',
    warning: '#f1653c',
    danger: '#ff5555',
    sky: '#73a1ff',
  }

  /** Defines the keys for the donut chart palette */
  private _donutChartColors = {
    domain: [
      '#52C2D5',
      '#6CCBDB',
      '#86D4E2',
      '#A0DDE8',
      '#BAE7EE',
      '#D4F0F4',
      '#EEF9FB',
    ],
  }

  /** Defines the keys for the tree map chart palette */
  private _treeMapChartColors = {
    domain: [
      '#289593',
      '#48A5A3',
      '#69B5B3',
      '#89C5C4',
      '#A9D5D4',
      '#C9E4E4',
      '#E9F4F4',
    ],
  }

  /** Defines the keys for the stacked bar chart palette */
  private _barStackedChartColors = {
    domain: ['#EE8D8B', '#F6C6C5', '#289593'],
  }

  /** Defines the keys for the comparator line chart palette */
  private _comparatorLineChartColors = {
    domain: ['#6A55AB', '#FFA726', '#73A1FF', '#CF8CB3', '#289593', '#DDA839'],
  }

  /** Defines the charts colors for the chart js libary */
  private _chartColors: ChartColors = {
    xLabelText: 'rgba(34, 34, 34, 0.6)',
    yLabelText: 'rgba(34, 34, 34, 0.6)',
    xTicks: 'rgba(34, 34, 34, 0.6)',
    yTicks: 'rgba(34, 34, 34, 0.6)',
    line: '#eeeeee',
  }

  /** The window object */
  private window: Window

  constructor() {
    const _document = this._document

    this.window = _document.defaultView as Window
    this._activeBreakpoint$.next(
      this.getActiveBreakpoint(this.window.innerWidth)
    )
    this.listenToScreenResize()
  }

  /** Getter for the chart colors displayed trhoughout the app */
  public get chartColors(): ChartColors {
    return this._chartColors
  }

  /** Getter for the dominant palette, read from the design tokens so it follows the active theme */
  public get palette(): Palette {
    const styles = this.window.getComputedStyle(this._document.documentElement)
    const read = (key: keyof Palette): string =>
      styles.getPropertyValue(this._paletteTokens[key]).trim() ||
      this._paletteFallback[key]

    return {
      primary: read('primary'),
      success: read('success'),
      info: read('info'),
      warning: read('warning'),
      danger: read('danger'),
      sky: read('sky'),
    }
  }

  /** Getter for the donut chart colors */
  public get donutChartColors(): { domain: string[] } {
    return this._donutChartColors
  }

  /** Getter for the tree map chart colors */
  public get treeMapChartColors(): { domain: string[] } {
    return this._treeMapChartColors
  }

  /** Getter for the bar stacked chart colors */
  public get barStackedChartColors(): { domain: string[] } {
    return this._barStackedChartColors
  }

  /** Getter for the coparator line chart colors */
  public get comparatorLineChartColors(): { domain: string[] } {
    return this._comparatorLineChartColors
  }

  /** Currently active breakpoint
   * @returns { Observable<Breakpoint> }
   */
  public get activeBreakpoint$(): Observable<Breakpoint> {
    return this._activeBreakpoint$.asObservable()
  }

  /** Currently active screensize
   * @returns { Observable<number> }
   */
  public get currentScreenSize$(): Observable<number> {
    return this._currentScreenSize$.asObservable()
  }

  /**
   * Group colors
   * @returns { { primary: string; secondary: string }[] }
   */
  public get groupColors(): { primary: string; secondary: string }[] {
    return this._comparatorLineChartColors.domain.map((color) => ({
      primary: color,
      secondary: this.hexToRgba(color, 0.1),
    }))
  }

  /** Listens to screen resizing to update active breakpoint */
  private listenToScreenResize(): void {
    fromEvent(window, 'resize')
      .pipe(debounceTime(250), untilDestroyed(this))
      .subscribe(() => {
        this._activeBreakpoint$.next(
          this.getActiveBreakpoint(this.window.innerWidth)
        )
      })
  }

  /** Calculates the active breakpoint depending on the current screen size
   * @param { number }
   * @returns { Breakpoint }
   */
  private getActiveBreakpoint(innerWidth: number): Breakpoint {
    let activeBreakpoint: Breakpoint = Breakpoint.Tiny

    for (const breakpoint of Object.values(Breakpoint)) {
      if (innerWidth > (breakpoint as Breakpoint)) {
        activeBreakpoint = breakpoint as Breakpoint
      }
    }

    this._currentScreenSize$.next(innerWidth)

    return activeBreakpoint
  }

  /**
   * Hex to rgba
   * @param { string } hex
   * @param { number } opacity
   * @returns { string }
   */
  private hexToRgba(hex: string, opacity: number): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)

    if (result) {
      return `rgba(${parseInt(result[1], 16)}, ${parseInt(
        result[2],
        16
      )}, ${parseInt(result[3], 16)}, ${opacity})`
    }
    return hex
  }
}
